import gql from "graphql-tag";
import {
  budgetFragment,
  projectFragment,
  PROJECT_DELIVERY_BUDGET_FRAGMENT,
} from "./fragments";

export const CREATE_PROJECT = gql`
  mutation createProject(
    $name: String!
    $description: String
    $clientId: ID!
    $defaultPaymentOption: PaymentOption!
    $budget: Int
    $withHourlyRate: Boolean
    $isCowork: Boolean
    $needsBudgetApproval: Boolean
    $color: String
  ) {
    createProject(
      name: $name
      description: $description
      clientId: $clientId
      defaultPaymentOption: $defaultPaymentOption
      budget: $budget
      withHourlyRate: $withHourlyRate
      isCowork: $isCowork
      needsBudgetApproval: $needsBudgetApproval
      color: $color
    ) {
      status
      error
      data {
        ...ProjectFragment
      }
    }
  }
  ${projectFragment}
`;

export const COPY_PROJECT = gql`
  mutation transferProject($id: ID!, $name: String!) {
    transferProject(id: $id, name: $name) {
      status
      error
      data {
        oldProject {
          id
          name
        }
        newProject {
          id
          name
          client {
            prefix
          }
        }
      }
    }
  }
`;

// ORCHA SIDE
export const COPY_PROJECT_PHASES = gql`
  mutation transferProjectPhases(
    $oldProjectId: ID!
    $newProjectId: ID!
    $slugPrefix: String
  ) {
    transferProjectPhases(
      oldProjectId: $oldProjectId
      newProjectId: $newProjectId
      slugPrefix: $slugPrefix
    ) {
      status
      error
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation updateProject(
    $id: ID!
    $name: String
    $description: String
    $clientId: ID
    $defaultPaymentOption: PaymentOption!
    $budget: Int
    $needsBudgetApproval: Boolean
    $budgetTracking: BudgetTracking!
    $plannedTimeTracking: PlannedTimeTracking!
    $estimationUnit: EstimationUnit!
    $color: String
    $velocity: Float
  ) {
    updateProject(
      id: $id
      name: $name
      description: $description
      clientId: $clientId
      defaultPaymentOption: $defaultPaymentOption
      budget: $budget
      needsBudgetApproval: $needsBudgetApproval
      budgetTracking: $budgetTracking
      plannedTimeTracking: $plannedTimeTracking
      estimationUnit: $estimationUnit
      color: $color
      velocity: $velocity
    ) {
      status
      error
      data {
        ...ProjectFragment
      }
    }
  }
  ${projectFragment}
`;

export const ARCHIVE_PROJECT = gql`
  mutation archiveProject($id: ID!) {
    archiveProject(id: $id) {
      status
      error
      data {
        ...ProjectFragment
      }
    }
  }
  ${projectFragment}
`;

export const UNARCHIVE_PROJECT = gql`
  mutation unarchiveProject($id: ID!) {
    unarchiveProject(id: $id) {
      status
      error
      data {
        ...ProjectFragment
      }
    }
  }
  ${projectFragment}
`;

export const APPROVE_BUDGET = gql`
  mutation approveBudget($id: ID!) {
    approveBudget(id: $id) {
      status
      error
      data {
        ...BudgetFragment
      }
    }
  }
  ${budgetFragment}
`;

export const CREATE_THEME = gql`
  mutation createTheme($projectId: ID!, $name: String!) {
    createTheme(projectId: $projectId, name: $name) {
      status
      error
      data {
        id
        name
      }
    }
  }
`;

export const MERGE_THEMES = gql`
  mutation mergeThemes(
    $themeIds: [String!]
    $projectId: ID!
    $newName: String!
  ) {
    mergeThemes(themeIds: $themeIds, projectId: $projectId, newName: $newName) {
      status
      error
      data {
        id
        name
      }
    }
  }
`;

export const UPDATE_THEME = gql`
  mutation updateTheme($id: ID!, $name: String!) {
    updateTheme(id: $id, name: $name) {
      status
      error
      data {
        id
        name
      }
    }
  }
`;

export const DELETE_THEME = gql`
  mutation deleteTheme($id: ID!) {
    deleteTheme(id: $id) {
      status
      error
      data {
        id
        name
      }
    }
  }
`;

export const SUBMIT_BUDGET = gql`
  mutation submitBudget($id: ID!) {
    submitBudget(id: $id) {
      status
      error
      data {
        id
        amount
        status
      }
    }
  }
`;

export const NEW_BUDGET = gql`
  mutation newBudget(
    $projectId: ID!
    $amount: Int!
    $themes: [BudgetThemeInput!]
  ) {
    newBudget(projectId: $projectId, amount: $amount, themes: $themes) {
      status
      error
      data {
        id
        amount
        status
        themes {
          theme {
            id
            name
          }
          amount
        }
      }
    }
  }
`;

export const UPDATE_BUDGET = gql`
  mutation updateBudget($id: ID!, $amount: Int!, $themes: [BudgetThemeInput!]) {
    updateBudget(id: $id, amount: $amount, themes: $themes) {
      status
      error
      data {
        id
        amount
        status
        themes {
          theme {
            id
            name
          }
          amount
        }
      }
    }
  }
`;

export const UPDATE_FAVORITE = gql`
  mutation updateFavorite($projectId: ID!, $value: Boolean!) {
    updateFavorite(projectId: $projectId, value: $value) {
      status
      error
    }
  }
`;

export const CREATE_PROJECT_RESOURCE = gql`
  mutation createProjectResource(
    $projectId: ID!
    $resourceLink: String!
    $resourceType: ProjectResourceType!
    $resourceCategory: ProjectResourceCategory!
    $label: String
    $tooltip: String
  ) {
    createProjectResource(
      projectId: $projectId
      resourceLink: $resourceLink
      resourceType: $resourceType
      resourceCategory: $resourceCategory
      label: $label
      tooltip: $tooltip
    ) {
      data {
        id
        label
        project {
          id
          name
        }
      }
      status
      error
    }
  }
`;

export const DELETE_PROJECT_RESOURCE = gql`
  mutation deleteProjectResource($selectedResource: ID!) {
    deleteProjectResource(resourceId: $selectedResource) {
      data {
        id
        label
      }
      status
      error
    }
  }
`;

export const ADD_PROJECT_TEAM_MEMBER = gql`
  mutation addProjectTeamMember($input: AddProjectTeamMemberInput!) {
    addProjectTeamMember(input: $input) {
      status
      error
      data {
        user {
          id
          firstName
          lastName
          avatarUrl
        }
        projectRole {
          id
          name
          hourlyRate
        }
        active
      }
    }
  }
`;

export const UPDATE_PROJECT_TEAM_MEMBER = gql`
  mutation updateProjectTeamMember($input: UpdateProjectTeamMemberInput!) {
    updateProjectTeamMember(input: $input) {
      status
      error
      data {
        user {
          id
          firstName
          lastName
          avatarUrl
        }
        projectRole {
          id
          name
          hourlyRate
          active
          startedAt
          endedAt
        }
        active
      }
    }
  }
`;
export const UPDATE_FAVORITE_THEMES = gql`
  mutation updateFavoriteThemes($favorites: [FavoriteTheme!]) {
    updateFavoriteThemes(favorites: $favorites) {
      status
      error
    }
  }
`;

export const SAVE_PROJECT_DELIVERY_ALLOCATION = gql`
  mutation saveProjectDeliveryAllocation(
    $input: SaveProjectDeliveryAllocationInput!
  ) {
    saveProjectDeliveryAllocation(input: $input) {
      ...ProjectDeliveryBudgetFragment
    }
  }
  ${PROJECT_DELIVERY_BUDGET_FRAGMENT}
`;

export const ADD_PROJECT_STAKEHOLDER = gql`
  mutation addProjectStakeholder($projectId: ID!, $userId: ID!, $role: Role!) {
    addProjectStakeholder(projectId: $projectId, userId: $userId, role: $role) {
      status
      error
      data {
        user {
          id
          firstName
          lastName
          avatarUrl
        }
        role
      }
    }
  }
`;

export const UPDATE_PROJECT_STAKEHOLDER = gql`
  mutation updateProjectStakeholder(
    $projectId: ID!
    $userId: ID!
    $oldRole: Role!
    $newRole: Role!
  ) {
    updateProjectStakeholder(
      projectId: $projectId
      userId: $userId
      oldRole: $oldRole
      newRole: $newRole
    ) {
      status
      error
      data {
        user {
          id
          firstName
          lastName
          avatarUrl
        }
        role
      }
    }
  }
`;

export const DELETE_PROJECT_STAKEHOLDER = gql`
  mutation deleteProjectStakeholder(
    $projectId: ID!
    $userId: ID!
    $role: Role!
  ) {
    deleteProjectStakeholder(
      projectId: $projectId
      userId: $userId
      role: $role
    ) {
      status
      error
      data {
        user {
          id
        }
        role
      }
    }
  }
`;
